import { TranslateService } from '@ngx-translate/core';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { State, FilterDescriptor } from '@progress/kendo-data-query';
import { EventListService } from './../core/service/grid-data/grid-data-event.service';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { SessionSetting } from '../core/model/session-setting';
import { SettingService } from '../core/service/setting.service';
import { MessageHandlerService } from '../core/utility/message-handler.service';
import { EventData, EventDetails, EventEdit } from './model/event';
import { MessengerService, MessageSubject } from '../core/service/messenger.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EventService } from '../core/service/event.service';
import { Subscription } from 'rxjs';
import { animations } from '../core/utility/animations';
import { Filter } from '../core/filter/filter';
import { FilterService } from '../core/filter/filter.service';
import { AppointmentService } from '../core/service/appointment.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { MemberService } from '../core/service/member.service';

@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],
  animations: [animations.fadeAnimation],
  providers: [EventListService]
})
export class EventComponent implements OnInit, OnDestroy, AfterViewChecked
{
  /**
   * Member Id
   */
  @Input() public memberId: number;

  /**
   * Case Id
   */
  @Input() public caseId: number;

  /**
   * Case Id
   */
  @Input() public screen = 'CASE_DETAILS';

  /**
   * Event list Kendo grid state.
   */

  @Output() public saveTask = new EventEmitter();
  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public pageSizes = [5, 10, 25, 50, 100];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public enableAdvanced = false;
  public taskDialogOpened = false;
  public pastDaysSelection: Array<number>;
  public selectedDays: number = null;
  public selectedEvent = { id: null, memberId: null, caseId: null };
  public eventTypes: Array<{ text: string; value: string; icon: string }>;
  public encounterTypes: Array<{ text: string; value: string }>;
  public appointmentStatuses: Array<{ text: string; value: string }>;
  public changes: any = {};
  public editForm: UntypedFormGroup;
  public editDataItem: any;
  public patientLabel = {};
  public cancelDataItem: any;
  public deleteDialogOpened = false;
  public appointmentToDelete: any;
  public resendAppointmentDialogOpened = false;
  public selectedAppointment: EventEdit;
  public canNotifyPatient = false;
  public notifyPatientType = '';
  public notifyPatientTypeLabel = {};
  public textEmail = '';
  public textSMS = '';

  /**
   * Appointment view type
   */
  public appointmentViewType = 'list';

  /**
   * When true, the saved event indicator is displayed.
   */
  public eventSaved = false;

  /**
   * Date last saved date
   */
  public eventSavedDate: Date;

  /**
   * New Appointment Event
   */
  public isNew: boolean;

  /**
   * This is to prevent the New Appointment double window when new is initiated from Care Team and View = Calendar.
   */
  public isNewClicked = false;

  /**
   * Session Setting object to control Idle configuration from back-end.
   */
  private sessionSetting: SessionSetting;
  private listSubscription: Subscription;
  private messagingSubscription: Subscription;

  constructor(
    private eventListService: EventListService,
    private eventService: EventService,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private formBuilder: UntypedFormBuilder,
    private filterService: FilterService,
    private changeDetectorRef: ChangeDetectorRef,
    private appointmentService: AppointmentService,
    private memberService: MemberService,
    private translate: TranslateService,
    public intl: IntlService)
  {
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'patientUpdated' &&
          message.data !== null && message.data.id === this.memberId)
        {
          // Updating canNotifyPatient if patient update have occurs.
          this.canNotifyPatient = message.data.notificationType !== null && message.data.notificationType !== 'NO_NOTIFICATION';
          this.notifyPatientType = this.canNotifyPatient ? message.data.notificationType : '';
        }
      }
    });
  }

  public get loading(): boolean
  {
    return this.eventListService.loading;
  }

  public ngOnInit(): void
  {
    this.getSessionSettings();

    this.encounterTypes = this.eventListService.encounterTypes;
    this.eventTypes = this.eventListService.eventTypes;
    this.appointmentStatuses = this.eventListService.appointmentStatuses;

    this.translate.get(['SMS', 'EMAIL']).subscribe({
      next: (res) =>
      {
        this.textEmail = res.EMAIL;
        this.textSMS = res.SMS;
      }
    });

    this.listSubscription = this.eventListService.subscribe({
      next: (value: EventData) =>
      {
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.eventDateTime = new Date(d.eventDateTime);
            d.eventTypeIcon = this.eventListService.getEventTypeIcon(d.eventType);
            d.eventTypeText = this.eventListService.getEventTypeText(d.eventType);
            d.encounterTypeText = this.eventListService.getEncounterTypeText(d.encounterType);
            if (d.appointment !== null && d.appointment !== undefined)
            {
              d.appointment.statusText = this.eventListService.getAppointmentStatusText(d.appointment.status);
            }
            d.resourceName = d.resource ? d.resource.name : null;
            d.metricName = d.resource ? d.resource.metric : null;
          }
          );
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };
      }
    });

    setTimeout(() =>
    {
      // For appointments we don't need to call getEvents since it is handled by viewModeChanged.
      // This is to prevent calling getEvents if the view mode default is the calendar.
      if (this.screen !== 'APPOINTMENTS' || this.appointmentViewType === 'list')
      {
        this.getEvents(this.memberId, this.caseId);
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Get events from API per the applied filter.
   *
   * @param filter Event filter string.
   */
  public getEvents(memberId: number, caseId: number): void
  {
    // hardcoded mechanism for 'my' cases to pick current user's name as manager.
    this.state = {
      filter: {
        filters: [
          { field: 'memberId', operator: 'eq', value: memberId },
        ], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'eventDateTime', dir: 'desc' }
      ],
      take: 5
    };

    if (this.screen === 'APPOINTMENTS')
    {
      this.state.filter.filters.push({ field: 'eventType', operator: 'eq', value: 'APPOINTMENT' });
      this.eventListService.queryByStateAndSource(this.state, 'patientSummary');
    }
    else
    {
      this.state.filter.filters.push({ field: 'caseId', operator: 'eq', value: caseId });
      this.getFilterScreen();
    }
    // Get the member to check the notificationType
    this.memberService.getMember(this.memberId).subscribe({
      next: (member) =>
      {
        this.canNotifyPatient = member.notificationType !== null && member.notificationType !== 'NO_NOTIFICATION';
        this.notifyPatientType = this.canNotifyPatient ? member.notificationType : '';
      }
    });
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(): void
  {
    // Not implemented
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(stateVar: DataStateChangeEvent): void
  {
    this.state = stateVar;
    this.runQuery();
  }

  /**
   * Click event when days is selected
   *
   * @param days Number of days to select
   */
  public pastDaysSelected(event: any, days: number): void
  {
    // If clicked on the same days, it will unselect
    if (days === this.selectedDays)
    {
      this.selectedDays = null;
    }
    else
    {
      this.selectedDays = days;
    }
    this.getEventsByPastDays(this.selectedDays);

    // Must use a setTimeout here otherwise component is not refreshed properly.
    setTimeout(() =>
    {
      if (this.selectedDays === null)
      {
        event.target.classList.remove('k-state-active');
      }
      else
      {
        event.target.classList.add('k-state-active');
      }
    });
  }

  /**
   * Refresh the events for the selected past days
   *
   * @param selectedDays Number of days selected
   */
  public getEventsByPastDays(selectedDays: number): void
  {
    if (!selectedDays)
    {
      // Remove pastDays filter
      this.state.filter.filters = this.state.filter.filters.filter((x: FilterDescriptor) => x.field !== 'pastDays');
    }
    else
    {
      // Add or Update pastDays filter
      const currentFilter: any = this.state.filter.filters.find((x: FilterDescriptor) => x.field === 'pastDays');
      if (currentFilter === undefined)
      {
        const filter = { field: 'pastDays', operator: 'lt', value: selectedDays };
        this.state.filter.filters.push(filter);
      }
      else
      {
        currentFilter.value = selectedDays;
      }
    }
    this.state.skip = 0;
    this.runQuery();
  }

  public initiateTask(selectedEvent: any): void
  {
    this.selectedEvent = selectedEvent;
    this.taskDialogOpened = true;
  }

  /**
   * Method called after a new task has been added.
   */
  public afterTaskAdded(message: any): void
  {
    this.taskDialogOpened = false;
    this.messengerService.sendMessage(new MessageSubject('addedTaskEvent', message));
    this.saveTask.emit(message);
    this.getEvents(this.memberId, this.caseId);
  }

  /**
   * Cell click handler to trigger updatable cell.
   */
  public cellClickHandler({ sender, rowIndex, column, columnIndex, dataItem, isEdited }): void
  {
    if (!isEdited && this.isEditable(column.field) &&
      (dataItem.eventType !== 'ENCOUNTER' && dataItem.eventType !== 'REFERRAL' && column.field === 'related'))
    {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
    }
  }

  public createFormGroup(dataItem: any): UntypedFormGroup
  {
    this.editForm = this.formBuilder.group({
      id: dataItem.id,
      version: dataItem.version,
      related: dataItem.related,
      caseId: this.caseId
    });
    return this.editForm;
  }

  public cellCloseHandler(args: any): void
  {
    const { formGroup } = args;

    if (!formGroup.valid)
    {
      args.preventDefault();
    }
    else
      if (formGroup.dirty)
      {
        this.eventService.updateEvent(formGroup.value).subscribe({
          next: (data) =>
          {
            this.eventSavedDate = new Date(data.lastModifiedOn);
            this.eventSaved = true;
            setTimeout(function ()
            {
              this.eventSaved = false;
            }.bind(this), 3000);

            this.runQuery();
          }
        });
      }
  }

  public isCaseEvent(caseId: number): boolean
  {
    return caseId === this.caseId;
  }

  public viewModeChanged(value: string): void
  {
    // If list mode changed, we must reload the grid automatically.
    if (value === 'list' && value !== this.appointmentViewType)
    {
      this.getEvents(this.memberId, this.caseId);
    }
    this.appointmentViewType = value;
  }

  public addEvent(): void
  {
    this.isNewClicked = true;
    this.isNew = true;
    this.editDataItem = {
      title: '',
      description: '',
      start: null,
      end: null,
      isAllDay: false
    };
  }

  public saveChangesHandler(): void
  {
    this.isNewClicked = false;
    this.editDataItem = undefined;
    this.getEvents(this.memberId, this.caseId);
    this.refresh.emit();
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
    this.isNewClicked = false;
  }

  public editHandler(dataItem: EventEdit): void
  {
    this.isNew = false;
    const editItem: any = {};
    editItem.dataItem = dataItem;
    editItem.id = dataItem.id;
    editItem.title = dataItem.appointment.title;
    editItem.description = dataItem.details;
    editItem.start = new Date(dataItem.eventDateTime);
    editItem.end = new Date(dataItem.eventEndDateTime);
    editItem.isAllDay = dataItem.appointment.allDay;
    this.editDataItem = editItem;
  }

  public canResendAppointment(dataItem: EventEdit): boolean
  {
    return (dataItem && dataItem.appointment && dataItem.appointment.sendAppointmentToPatient && this.canNotifyPatient);
  }

  public confirmResendAppointment(dataItem: EventEdit): void
  {
    this.notifyPatientTypeLabel = { type: this.notifyPatientType === 'SMS' ? this.textSMS : this.textEmail };
    this.resendAppointmentDialogOpened = true;
    this.selectedAppointment = dataItem;
  }

  public cancelAppointment(dataItem: EventEdit): void
  {
    this.cancelDataItem = dataItem;
  }

  public saveCancelHandler(): void
  {
    this.cancelDataItem = undefined;
    this.getEvents(this.memberId, this.caseId);
    this.refresh.emit();
  }

  public cancelCancelHandler(): void
  {
    this.cancelDataItem = undefined;
  }

  public getEventIconTitle(dataItem: EventDetails): string
  {
    const eventTypeText = dataItem?.eventTypeText;
    const encounterTypeText = dataItem?.encounterTypeText;
    const encounterText = encounterTypeText !== null ? `(${encounterTypeText})` : '';
    const appointmentStatusText = dataItem?.appointment && dataItem?.appointment.statusText ? `(${dataItem?.appointment.statusText})` : '';

    return `${eventTypeText} ${encounterText} ${appointmentStatusText}`;
  }

  public openDeleteAppointment(event: any): void
  {
    // Validate date range
    if (event !== null)
    {
      this.appointmentToDelete = event;
      this.deleteDialogOpened = true;
    }
  }

  public deleteAppointment(): void
  {
    // Validate date range
    this.appointmentService.deleteAppointment(this.appointmentToDelete.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('APPOINTMENT-DELETED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.appointmentToDelete = undefined;
        this.getEvents(this.memberId, this.caseId);
        this.refresh.emit();
        this.deleteDialogOpened = false;
      }
    });
  }

  public schedulerOnRefresh(): void
  {
    this.refresh.emit();
  }

  public resendAppointmentEmail(): void
  {
    this.appointmentService.resendAppointment(this.selectedAppointment.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('APPOINTMENT-RESENT-SUCCESSFULLY');
        this.refresh.emit();
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
    this.resendAppointmentDialogOpened = false;
    this.selectedAppointment = undefined;
  }

  /**
   * Check if the column is editable.
   *
   * @param field Grid column
   * @returns boolean
   */
  private isEditable(field: string): boolean
  {
    const editableColumns = ['related'];
    return editableColumns.indexOf(field) > -1;
  }

  /**
   * Get the session settings used to pastDaysSelection filter.
   */
  private getSessionSettings(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (data) =>
      {
        this.sessionSetting = data;
        this.pastDaysSelection = this.sessionSetting.eventPastDaysFilter;
        this.patientLabel = { patientLabel: data.patientLabel.toLowerCase() };
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private getFilterScreen()
  {
    this.filterService.getFilterByScreen('Event', this.screen).subscribe({
      next: (data: Filter) =>
      {
        if (data !== null)
        {
          const filterState = JSON.parse(data.state);
          if (filterState !== null)
          {
            filterState.filter.filters.forEach(f => this.state.filter.filters.push(f));
          }
        }
        this.eventListService.queryByState(this.state);
      }
    });
  }

  private runQuery(): void
  {
    if (this.screen === 'APPOINTMENTS')
    {
      this.eventListService.queryByStateAndSource(this.state, 'patientSummary');
    }
    else
    {
      this.eventListService.queryByState(this.state);
    }
  }
}
